/**
 * Panoramica di gruppo: una riga per atleta, confrontabile a colpo d'occhio.
 * Ultima attivita' nota, giornate assegnate con l'esito registrato (numeratore
 * e denominatore, mai una percentuale sola), attivita' extra, copertura del
 * diario benessere e segnalazioni con il motivo. Nessuna classifica.
 */
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight, Info, MessageSquare } from "lucide-react";
import {
	coverageSentence,
	daysSinceLabel,
	formatShortDay,
} from "../../lib/monitoringUtils";
import { formatFullName } from "../../lib/utils";
import { Button } from "../ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { FlagChip, useIsCompact } from "./primitives";

// Le colonne che ordinano, nell'ordine delle intestazioni: sul telefono le
// stesse chiavi stanno in un selettore.
const SORT_COLUMNS = [
	["attention", "monSortAttention"],
	["name", "monColAthlete"],
	["flags", "monColFlags"],
	["last_activity", "monColLastActivity"],
	["coverage", "monColOutcome"],
	["extra", "monColExtra"],
	["wellness", "monColWellness"],
];

function OutcomeBar({ row, width = "w-28" }) {
	const total = Number(row.assigned_past || 0);
	if (!total) return <div className={`h-1.5 ${width} rounded-sm bg-muted/50`} aria-hidden="true" />;
	const segment = (count, cls) =>
		count ? (
			<div className={`h-full rounded-[1px] ${cls}`} style={{ flexGrow: count }} />
		) : null;
	return (
		<div className={`flex h-1.5 ${width} gap-[2px]`} aria-hidden="true">
			{segment(row.complete, "bg-volt")}
			{segment(row.partial, "bg-amber-400")}
			{segment(row.missing, "bg-blaze/80")}
		</div>
	);
}

function WellnessCell({ row, t, language }) {
	const wellness = row.wellness;
	if (!wellness) return <span className="text-muted-foreground">—</span>;
	if (!wellness.enabled) return <span className="text-muted-foreground">{t("monWellnessOff")}</span>;
	return (
		<div className="text-xs">
			<div>
				{wellness.entries}/{wellness.days_in_window} {t("monWellnessCheckins")}
			</div>
			{wellness.last_entry_date ? (
				<div className="text-muted-foreground">
					{t("monWellnessLast")} {formatShortDay(wellness.last_entry_date, language)}
				</div>
			) : null}
		</div>
	);
}

function SortHeader({ label, sortKey, sort, onSort, className }) {
	const active = sort?.key === sortKey;
	const Icon = !active ? ArrowUpDown : sort.direction === "desc" ? ArrowDown : ArrowUp;
	return (
		<th className={className} aria-sort={active ? (sort.direction === "desc" ? "descending" : "ascending") : "none"}>
			<button
				type="button"
				onClick={() => onSort(sortKey)}
				className={`inline-flex items-center gap-1 uppercase tracking-widest ${active ? "text-foreground" : ""}`}
				data-testid={`mon-sort-${sortKey}`}
			>
				{label}
				<Icon className="w-3 h-3" aria-hidden="true" />
			</button>
		</th>
	);
}

/** L'ordinamento sul telefono: la colonna in un selettore, il verso in un tasto. */
function SortBar({ sort, onSort, t }) {
	const descending = sort?.direction === "desc";
	return (
		<div className="flex items-center gap-2" data-testid="mon-sort-bar">
			<span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">{t("monSortBy")}</span>
			<Select value={sort?.key} onValueChange={(key) => key !== sort?.key && onSort(key)}>
				<SelectTrigger className="rounded-sm h-9 flex-1 min-w-0" aria-label={t("monSortBy")}>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					{SORT_COLUMNS.map(([key, labelKey]) => (
						<SelectItem key={key} value={key}>
							{t(labelKey)}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			<Button
				type="button"
				variant="outline"
				size="icon"
				className="rounded-sm h-9 w-9 shrink-0"
				onClick={() => onSort(sort?.key)}
				aria-label={t(descending ? "monSortDescending" : "monSortAscending")}
				data-testid="mon-sort-direction"
			>
				{descending ? <ArrowDown className="w-4 h-4" aria-hidden="true" /> : <ArrowUp className="w-4 h-4" aria-hidden="true" />}
			</Button>
		</div>
	);
}

/**
 * Quale ordine vale, detto in chiaro. L'attenzione non e' una colonna: quando il
 * coach ordina per un'altra, un tasto riporta all'ordine predefinito.
 */
function SortNote({ sort, onSort, t }) {
	if (sort?.key === "attention") {
		return <p className="text-xs text-muted-foreground" data-testid="mon-sort-note">{t("monSortAttentionNote")}</p>;
	}
	return (
		<p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2" data-testid="mon-sort-note">
			<span>{t("monSortCustomNote")}</span>
			<button
				type="button"
				onClick={() => onSort("attention")}
				className="underline underline-offset-2 hover:text-foreground"
				data-testid="mon-sort-reset"
			>
				{t("monSortResetAttention")}
			</button>
		</p>
	);
}

/** Un atleta in una scheda: le stesse informazioni della riga, una sotto l'altra. */
function OverviewCard({ row, t, language, onSelect }) {
	const wellness = row.wellness;
	return (
		<li>
			<button
				type="button"
				onClick={() => onSelect(row.athlete_id)}
				className="w-full text-left rounded-sm border border-border bg-card px-3 py-3 space-y-2 active:bg-muted/40"
				data-testid={`mon-card-${row.athlete_id}`}
			>
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<div className="font-medium truncate">{formatFullName(row)}</div>
						{row.email ? <div className="text-xs text-muted-foreground truncate">{row.email}</div> : null}
					</div>
					<div className="text-right shrink-0">
						<div className="text-xs">{row.last_activity_date ? formatShortDay(row.last_activity_date, language) : "—"}</div>
						<div className="text-[11px] text-muted-foreground">{daysSinceLabel(row, t)}</div>
					</div>
				</div>
				{row.flags?.length ? (
					<div className="flex flex-wrap gap-1">
						{row.flags.map((flag) => (
							<FlagChip key={flag.code} flag={flag} t={t} language={language} />
						))}
					</div>
				) : null}
				<OutcomeBar row={row} width="w-full" />
				<div className="text-xs">
					{coverageSentence(row, t)}
					{row.upcoming ? <span className="text-muted-foreground"> · {row.upcoming} {t("monUpcomingShort")}</span> : null}
				</div>
				{row.extra_days || wellness || row.undated_programs ? (
					<div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
						{row.extra_days ? (
							<span>
								{row.extra_days} {t("monExtraDays")}
							</span>
						) : null}
						{wellness ? (
							<span>
								{t("monColWellness")}:{" "}
								{wellness.enabled
									? `${wellness.entries}/${wellness.days_in_window} ${t("monWellnessCheckins")}`
									: t("monWellnessOff")}
							</span>
						) : null}
						{row.undated_programs ? (
							<span className="inline-flex items-center gap-1">
								<Info className="w-3 h-3" aria-hidden="true" />
								{t("monUndatedShort")}
							</span>
						) : null}
					</div>
				) : null}
			</button>
		</li>
	);
}

export function MonitoringOverviewTable({ rows, t, language, onSelect, onWrite, sort, onSort }) {
	const compact = useIsCompact();
	if (!rows.length) {
		return (
			<div className="rounded-sm border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
				{t("monEmpty")}
			</div>
		);
	}
	if (compact) {
		return (
			<div className="space-y-2">
				<SortBar sort={sort} onSort={onSort} t={t} />
				<SortNote sort={sort} onSort={onSort} t={t} />
				<ul className="space-y-2" data-testid="mon-overview-cards">
					{rows.map((row) => (
						<OverviewCard key={row.athlete_id} row={row} t={t} language={language} onSelect={onSelect} />
					))}
				</ul>
			</div>
		);
	}
	const head = "px-3 py-2 text-left text-[10px] uppercase tracking-widest text-muted-foreground font-medium whitespace-nowrap";
	return (
		<div className="space-y-2">
			<SortNote sort={sort} onSort={onSort} t={t} />
			<div className="rounded-sm border border-border bg-card overflow-x-auto">
				<table className="w-full text-sm">
					<thead className="border-b border-border">
						<tr>
							<SortHeader label={t("monColAthlete")} sortKey="name" sort={sort} onSort={onSort} className={head} />
							<SortHeader label={t("monColFlags")} sortKey="flags" sort={sort} onSort={onSort} className={head} />
							<SortHeader label={t("monColLastActivity")} sortKey="last_activity" sort={sort} onSort={onSort} className={head} />
							<SortHeader label={t("monColOutcome")} sortKey="coverage" sort={sort} onSort={onSort} className={head} />
							<SortHeader label={t("monColExtra")} sortKey="extra" sort={sort} onSort={onSort} className={head} />
							<SortHeader label={t("monColWellness")} sortKey="wellness" sort={sort} onSort={onSort} className={head} />
							<th className={head} aria-label={t("monOpenAthlete")} />
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<tr
								key={row.athlete_id}
								className="border-b border-border/60 last:border-b-0 hover:bg-muted/30 cursor-pointer"
								onClick={() => onSelect(row.athlete_id)}
								data-testid={`mon-row-${row.athlete_id}`}
							>
								<td className="px-3 py-2.5 align-top">
									<div className="font-medium truncate max-w-[220px]">{formatFullName(row)}</div>
									{row.email ? (
										<div className="text-xs text-muted-foreground truncate max-w-[220px]">{row.email}</div>
									) : null}
									{row.undated_programs ? (
										<div
											className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground"
											title={t("monUndatedPrograms")}
										>
											<Info className="w-3 h-3 shrink-0" aria-hidden="true" />
											{t("monUndatedShort")}
										</div>
									) : null}
								</td>
								<td className="px-3 py-2.5 align-top min-w-[200px] max-w-[280px]">
									{row.flags?.length ? (
										<div className="flex flex-wrap gap-1">
											{row.flags.map((flag) => (
												<FlagChip key={flag.code} flag={flag} t={t} language={language} />
											))}
										</div>
									) : (
										<span className="text-muted-foreground">—</span>
									)}
								</td>

								<td className="px-3 py-2.5 align-top whitespace-nowrap">
									<div>{row.last_activity_date ? formatShortDay(row.last_activity_date, language) : "—"}</div>
									<div className="text-xs text-muted-foreground">{daysSinceLabel(row, t)}</div>
								</td>
								<td className="px-3 py-2.5 align-top min-w-[220px]">
									<OutcomeBar row={row} />
									<div className="text-xs mt-1">{coverageSentence(row, t)}</div>
									{row.upcoming ? (
										<div className="text-[11px] text-muted-foreground">
											{row.upcoming} {t("monUpcomingShort")}
										</div>
									) : null}
								</td>
								<td className="px-3 py-2.5 align-top whitespace-nowrap">
									{row.extra_days ? (
										<span className="tabular-nums">
											{row.extra_days} <span className="text-xs text-muted-foreground">{t("monExtraDays")}</span>
										</span>
									) : (
										<span className="text-muted-foreground">—</span>
									)}
								</td>
								<td className="px-3 py-2.5 align-top whitespace-nowrap">
									<WellnessCell row={row} t={t} language={language} />
								</td>
								<td className="px-2 py-2 align-top text-muted-foreground whitespace-nowrap">
									<div className="flex items-center justify-end gap-1">
										{onWrite ? (
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className="rounded-sm h-8 w-8"
												onClick={(event) => {
													event.stopPropagation();
													onWrite(row);
												}}
												aria-label={`${t("monWriteTo")} ${formatFullName(row)}`}
												title={`${t("monWriteTo")} ${formatFullName(row)}`}
												data-testid={`mon-write-${row.athlete_id}`}
											>
												<MessageSquare className="w-4 h-4" aria-hidden="true" />
											</Button>
										) : null}
										<ChevronRight className="w-4 h-4" aria-hidden="true" />
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}
